
import React, { useEffect, useRef, useState } from 'react';
import {
  FlexibleXYPlot,
  Hint,
  XAxis,
  YAxis,
  VerticalGridLines,
  HorizontalGridLines,
  MarkSeries
} from 'react-vis';
import { Card, Spinner } from 'react-bootstrap';
import { GoGraph } from 'react-icons/go';
import { getScatterVisData, isAggregateRowLabel } from './EntityPanelUtils';
import { overviewTableKey } from 'DataDiscovery/discoverApi';
import { commafy, truncateCenter, uiLabel } from 'Utils';
import { BsSearch, BsSortAlphaDown, BsSortNumericDown } from 'react-icons/bs';
import { GoFileZip } from 'react-icons/go';
import { Link } from 'react-router-dom';
import DiscoveryTooltip from 'DiscoveryTooltip';
import {
  archiveInnerPath,
  archiveOuterName,
  compactArchiveLabel,
  isArchivePath,
} from 'Utils/archivePath';
import { formatTextColor } from 'Utils/fileTypeUtils';

// Compact label for an archive-path tick. Preserves the archive boundary
// instead of letting truncateCenter chew through it. The leading file-zip
// icon is rendered separately by tickFormat (foreignObject) so it matches
// the icon used in TablePanel / CandidateSourcesPanel.
function tickLabelForTable(name, max = 25) {
  if (!isArchivePath(name)) {
    return truncateCenter(name, max, '..');
  }
  const outer = archiveOuterName(name);
  const inner = archiveInnerPath(name);
  if (!inner) {
    return truncateCenter(outer, max, '..');
  }
  const remaining = Math.max(8, max - outer.length - 3);
  const innerCompact = truncateCenter(inner, remaining, '..');
  return `${outer} / ${innerCompact}`;
}

// Two-line folder-path subtitle for a file row: the first two path segments on the
// first line, the last two on the second, with an ellipsis marking anything elided
// between them. Short paths (≤2 segments) collapse to one line. The full path is
// available on hover (tooltip) — these lines only need to orient the reader.
export function buildPathSubtitleLines(dirPath) {
  const lead = dirPath.startsWith('/') ? '/' : '';
  const segs = dirPath.split('/').filter(Boolean);
  if (segs.length === 0) return [dirPath || '/'];
  if (segs.length <= 2) return [lead + segs.join('/')];
  if (segs.length <= 4) return [lead + segs.slice(0, 2).join('/'), segs.slice(2).join('/')];
  return [`${lead}${segs.slice(0, 2).join('/')}/…`, `…/${segs.slice(-2).join('/')}`];
}

const EntityMarkSeries = (props) => {
  const [margin, setMargin] = useState({});
  const [height, setHeight] = useState(200);
  const [xTickPositions, setXTickPositions] = useState([]);
  const [xTickWords, setXTickWords] = useState([]);
  const [yTickPositions, setYTickPositions]= useState([]);
  const [yTickWords, setYTickWords] = useState([]);
  const [data, setData] = useState([]);
  const [tooltipData, setTooltipData] = useState(null);
  const [tableRowFindingsLabel, setTableRowFindingsLabel] = useState({});
  const [searchField, setSearchField] = useState('');
  // Y-axis (table/file) ordering: 'count' by total findings, 'name' alphabetical.
  const [sortBy, setSortBy] = useState('count');
  const [sortDir, setSortDir] = useState('asc');

  const searchid = props.searchid;
  const loading = props.loading;
  const dataLabel = props.dataLabel;
  const entity = props.entity;
  const tableTotalCounts = (props.entityTables && props.entityTables.TOTAL) || {};
  const lowConfidence = props.lowConfidence;
  const tablesToSampledRows = props.tablesToSampledRows || {};

  // Both maps are keyed by overviewTableKey — the same (possibly schema-qualified) key
  // getScatterVisData uses for the Y-axis rows, so same-named tables in different
  // schemas stay distinct rows here too.
  //
  // typeByTable: file-source rows carry the worker's content-sniffed format
  // (sampler_type) — key the tick label color off it so names match the FileTypeChip
  // palette. Classic DB tables have no sampler_type and keep the default link blue;
  // defaultSamplerType lets Folder Search callers claim legacy document-only rows
  // (pre-2026-07-15 runs stamped nothing).
  //
  // schemaByTable: the schema label shown under each table tick in place of the
  // findings-count subtitle. File/NoSQL/owner-less rows carry no `schema` and keep the
  // findings subtitle.
  const typeByTable = {};
  const schemaByTable = {};
  (props.overviewTables || []).forEach((t) => {
    const key = overviewTableKey(t);
    if (!key) return;
    const samplerType = t.sampler_type || props.defaultSamplerType;
    if (samplerType) typeByTable[key] = samplerType;
    if (t.schema) schemaByTable[key] = t.schema;
  });

  useEffect(() => {
    try {
      const {
        marginValues,
        heightValue,
        xTickPositionsValues,
        xTickWordsValues,
        yTickPositionsValues,
        yTickWordsValues,
        dataValues,
        tableRowFindingsLabel: rowLabels
      } = getScatterVisData(
        props.entity,
        props.overviewTables || [],
        lowConfidence,
        props.entityTables || {},
        {
          sortBy,
          sortDir,
          // Server-search callers already loaded a server-filtered page — filtering
          // again client-side would hide rows outside that page's own match set.
          filterTerm: props.onServerSearch ? '' : searchField,
          allEntities: props.allEntities,
          dataLabel: props.dataLabel,
        }
      );

      setMargin(marginValues);
      setHeight(heightValue);
      setXTickPositions(xTickPositionsValues);
      setXTickWords(xTickWordsValues);
      setYTickPositions(yTickPositionsValues);
      setYTickWords(yTickWordsValues);
      setData(dataValues);
      setTableRowFindingsLabel(rowLabels || {});
    } catch (e) {
      console.log(e);
    }
    // Index-mode (onServerSearch) callers already fetched a server-filtered page, so the
    // local filterTerm is intentionally dropped in that branch above — searchField still
    // drives the debounced server refetch, just not the client-side filter here.
  }, [props.entity, props.overviewTables, props.entityTables, lowConfidence, sortBy, sortDir, searchField, props.allEntities, props.onServerSearch]);

  // Toggle a sort key: clicking the active key flips direction; switching keys
  // resets to that key's natural default (count -> descending, name -> A→Z).
  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir(key === 'count' ? 'desc' : 'asc');
    }
  };

  // Index-mode: the search box (below) refetches server-side instead of filtering the
  // already-loaded page — a match outside the loaded top-N would otherwise be invisible.
  // Debounced so keystrokes don't each fire a request.
  const serverSearchDebounceRef = useRef(null);
  useEffect(() => () => {
    if (serverSearchDebounceRef.current) clearTimeout(serverSearchDebounceRef.current);
  }, []);
  const handleSearchInputChange = (value) => {
    setSearchField(value);
    if (!props.onServerSearch) return;
    if (serverSearchDebounceRef.current) clearTimeout(serverSearchDebounceRef.current);
    serverSearchDebounceRef.current = setTimeout(() => props.onServerSearch(value), 400);
  };

  const sortBtnStyle = (active) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    cursor: 'pointer',
    border: '1px solid #E5E8ED',
    borderRadius: 8,
    background: active ? '#E9EEFF' : '#fff',
    color: active ? '#1E40AF' : '#5C6473',
    fontSize: 13,
    padding: '5px 10px',
  });

  const arrow = (key) => (sortBy === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '');

  const renderBody = () => (
    <>
      { loading ? (
        <div className='d-flex justify-content-center'>
          <Spinner variant='primary' animation='border' />
        </div>
      ) : yTickWords.length === 0 ? (
        <div className='text-center' style={{ padding: 24, fontSize: 14, color: '#8A92A0' }}>
          { searchField.trim()
            ? `No ${dataLabel} match “${searchField.trim()}”.`
            : `No ${dataLabel} with findings to display.` }
        </div>
      ) : (
        <div>
          <FlexibleXYPlot
            margin={margin}
            height={height}
            xDomain={[0, Math.max(xTickPositions.length, 1) + 1]}
            yDomain={[0, Math.max(yTickPositions.length, 1) + 1]}
          >
            <VerticalGridLines />
            <HorizontalGridLines />
            <XAxis
              tickLabelAngle={-45}
              tickValues={xTickPositions}
              tickFormat={v => { return xTickWords[v - 1]}}
              style={{
                title: {
                  fontSize: '14px',
                  transform: 'translateX(-42px) translateY(32px) rotate(-45deg)'
                }
              }}
              title={(
                  <tspan dy='60px'>Entities</tspan>
                )}
              orientation='top'
              position='start'
            />
            <YAxis
              hideLine
              className='text-capitalize'
              tickValues={yTickPositions}
              style={{
                title: {
                  fontSize: '14px',
                  transform: 'rotate(180deg)'
                }
              }}
              title={(<tspan dx='115px' dy='10px'>{dataLabel}</tspan>)}
              position='end'
              tickFormat={v => {
                const name = yTickWords[v - 1];
                const findingsCount = tableRowFindingsLabel[name] ??
                  (entity === 'ALL'
                    ? tableTotalCounts[name]
                    : (props.entityTables && props.entityTables[entity] && props.entityTables[entity][name]));
                // Cap overflow row (getScatterVisData, 200+ tables) — an aggregate
                // summary, not a real table, so it renders as plain text with no link.
                if (isAggregateRowLabel(name)) {
                  return (
                    <foreignObject x={-233} y={-18} width={228} height={48}>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-end',
                          textAlign: 'right',
                          fontSize: '12px',
                          lineHeight: '1.2',
                          color: '#8A92A0',
                          fontStyle: 'italic',
                        }}
                      >
                        <span>{name}</span>
                        <span>{`( ${commafy(findingsCount)} findings )`}</span>
                      </div>
                    </foreignObject>
                  );
                }
                const archive = isArchivePath(name);
                const nameColor = formatTextColor(typeByTable[name]) || '#315DFF';
                // `name` is the row key — `schema.table` for schema-carrying rows. Show
                // the bare table name as the link label and the schema as the grey
                // subtitle; schema-less rows keep the findings-count subtitle.
                const schema = schemaByTable[name];
                const displayName = schema ? name.slice(schema.length + 1) : name;
                // File rows: the key is the file's path. Show just the file name as the
                // link label and the containing folder as a two-line grey subtitle
                // (hover reveals the full path). Path-less file keys keep the
                // findings-count subtitle like any other schema-less row.
                const fileSlashIdx = dataLabel === 'files' ? name.lastIndexOf('/') : -1;
                const isFileRow = fileSlashIdx > -1;
                const fileName = isFileRow ? name.slice(fileSlashIdx + 1) : null;
                const pathLines = isFileRow
                  ? buildPathSubtitleLines(name.slice(0, fileSlashIdx) || '/')
                    .map((l) => truncateCenter(l, 34, '..'))
                  : null;
                return (
                  <foreignObject x={-233} y={isFileRow ? -22 : -18} width={228} height={isFileRow ? 58 : 48}>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        textAlign: 'right',
                        fontSize: '12px',
                        lineHeight: '1.2',
                      }}
                    >
                      <DiscoveryTooltip text={archive
                        ? `View findings in ${compactArchiveLabel(name)}`
                        : `View findings in ${name}`}>
                        <Link
                          className='scatterplot-tick-links'
                          to={`/results/${searchid}/${entity}/${name}`}
                          style={{ color: nameColor, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                        >
                          {archive && (
                            <GoFileZip aria-label='Inside archive' style={{ flexShrink: 0 }} />
                          )}
                          <span>
                            {isFileRow
                              ? truncateCenter(fileName, 25, '..')
                              : tickLabelForTable(displayName, 25)}
                          </span>
                        </Link>
                      </DiscoveryTooltip>
                      {isFileRow ? (
                        <DiscoveryTooltip text={name} placement='top'>
                          <span style={{ color: '#8A92A0', fontSize: '11px', lineHeight: '1.15', whiteSpace: 'nowrap' }}>
                            {pathLines.length === 1 ? `( ${pathLines[0]} )` : (
                              <>
                                {`( ${pathLines[0]}`}
                                <br />
                                {`${pathLines[1]} )`}
                              </>
                            )}
                          </span>
                        </DiscoveryTooltip>
                      ) : schema ? (
                        <span style={{ color: '#8A92A0' }}>{schema}</span>
                      ) : (
                        <span>{`( ${commafy(findingsCount)} findings )`}</span>
                      )}
                    </div>
                  </foreignObject>
                );
              }}
            />
            <MarkSeries
              onValueMouseOver={(value) => setTooltipData(value)}
              onValueMouseOut={() => setTooltipData(null)}
              // Each point carries a literal severity hex from SEVERITY_RAMP
              // (getScatterVisData) — no interpolated colorRange.
              colorType='literal'
              strokeWidth={2}
              // Max radius kept well under the 40px row pitch (see heightValue in
              // getScatterVisData) so the largest marks leave a gap and don't overlap.
              sizeRange={[2, 16]}
              data={data}
            />
            { tooltipData && (
              <Hint value={tooltipData}>
                <div
                  className='timeseries-tooltip'
                >
                  <div>
                    {lowConfidence && (<div><b>LOW INCIDENCE FINDING</b></div>)}
                    <div>
                      <span>
                        {Array.isArray(tooltipData.fieldNames) && tooltipData.fieldNames.length > 1 ? (
                          <>
                            <b>{tooltipData.fieldNames.length}</b> fields ({tooltipData.fieldNames.join(', ')})
                            <br />
                          </>
                        ) : (
                          <>
                            Field <b>{(tooltipData.fieldNames && tooltipData.fieldNames[0]) || tooltipData.fieldName}</b>
                            <br />
                          </>
                        )}
                        Found {commafy(tooltipData.count)} instances of {uiLabel(tooltipData.entityValue)} in{' '}
                        {isArchivePath(tooltipData.table) ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <GoFileZip aria-label='Inside archive' style={{ flexShrink: 0 }} />
                            <b>{compactArchiveLabel(tooltipData.table)}</b>
                          </span>
                        ) : (
                          <b>{tooltipData.table}</b>
                        )}
                      </span>
                      <span>
                        <br />
                        ({tablesToSampledRows[tooltipData.table] ?
                          tablesToSampledRows[tooltipData.table].table_sampled_row_qty : 0} sampled rows)
                      </span>
                    </div>
                  </div>
                </div>
              </Hint>
            )}
          </FlexibleXYPlot>
        </div>
      )}
    </>
  );

  // Embedded mode (hideHeader): the folder-grouped overview renders one scatter per
  // expanded folder and provides its own search/sort header at the panel level, so
  // the per-instance Card chrome and header controls are dropped.
  if (props.hideHeader) {
    return (
      <div className='entity-mark-series-container text-left'>
        {renderBody()}
      </div>
    );
  }

  return (
    <Card className="shadow-sm widget-card text-left">
      <div className="widget-header d-flex align-items-center p-3" style={{ marginBottom: '10px', flexWrap: 'wrap', gap: 12 }}>
        <div className='d-flex align-items-center' style={{ whiteSpace: 'nowrap' }}>
          <GoGraph className="mr-1 widget-header-icon widget-header-icon-lg" />
          Sensitive Data Distribution
        </div>
        <div className='d-flex align-items-center ml-auto' style={{ gap: 8, flexWrap: 'wrap' }}>
          <button
            type='button'
            style={sortBtnStyle(sortBy === 'name')}
            onClick={() => handleSort('name')}
            title='Sort by name'
          >
            <BsSortAlphaDown size={14} />
            Name{arrow('name')}
          </button>
          <button
            type='button'
            style={sortBtnStyle(sortBy === 'count')}
            onClick={() => handleSort('count')}
            title='Sort by number of findings'
          >
            <BsSortNumericDown size={14} />
            Findings{arrow('count')}
          </button>
          <div style={{ position: 'relative', minWidth: 200, maxWidth: 300 }}>
            <BsSearch size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#8A92A0' }} />
            <input
              data-testid='search-overview-scatter'
              value={searchField}
              onChange={(e) => handleSearchInputChange(e.target.value)}
              placeholder={`Search ${dataLabel}…`}
              style={{
                width: '100%', borderRadius: 8, border: '1px solid #E5E8ED', background: '#fff',
                padding: '6px 12px 6px 30px', fontSize: 14, outline: 'none',
              }}
            />
          </div>
        </div>
        {props.subHeaderNote && (
          <div className='w-100' style={{ fontSize: 12, color: '#5C6473' }}>
            {props.subHeaderNote}
          </div>
        )}
      </div>
      <Card.Body className="entity-mark-series-container">
        {renderBody()}
      </Card.Body>
    </Card>
  );
}

export default EntityMarkSeries;